import { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { useYupValidationResolver } from "shared/ui/rjsf-form/hooks/useYupValidationResolver"

import { ShareLinkInviteEmailFormData } from "features/project-sharing/model/useSendShareLinkInvite"
import { AnySchema, object, string } from "yup"

import {
  ShareProjectOverlayProps,
  ShareProjectOverlayTab
} from "../ShareProjectOverlay"
import { ShareProjectOverlayViewModel } from "../ShareProjectOverlayView"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Shape<Fields extends Record<string, any>> = {
  [Key in keyof Fields]: AnySchema<Fields[Key]>
}

const schema = object<Shape<ShareLinkInviteEmailFormData>>({
  email: string().required().email(),
  comment: string().optional(),
  projectID: string().required()
})

export const useShareProjectOverlayViewModel = ({
  defaultFormData,
  onSubmit,
  ...rest
}: ShareProjectOverlayProps): ShareProjectOverlayViewModel => {
  const { t } = useTranslation()
  const [activeKey, setActiveKey] = useState<ShareProjectOverlayTab>(
    ShareProjectOverlayTab.LINK
  )
  const resolver = useYupValidationResolver(schema)
  const emailForm = useForm<ShareLinkInviteEmailFormData>({
    defaultValues: defaultFormData,
    resolver
  })

  useEffect(() => {
    emailForm.reset(defaultFormData)
  }, [defaultFormData])

  const handleSubmit = useMemo(() => {
    return emailForm.handleSubmit(onSubmit)
  }, [emailForm.handleSubmit, onSubmit])

  const handleActiveKeyChange = useEvent(
    (nextActiveKey: ShareProjectOverlayTab | null) => {
      setActiveKey(prev => nextActiveKey ?? prev)
    }
  )

  const options = useMemo(
    () => [
      { label: t("Project.ShareViaLink"), value: ShareProjectOverlayTab.LINK },
      { label: t("Project.ShareViaEmail"), value: ShareProjectOverlayTab.EMAIL }
    ],
    [t]
  )

  return {
    ...rest,
    options,
    activeKey,
    onActiveKeyChange: handleActiveKeyChange,
    emailForm,
    onSubmit: handleSubmit
  }
}
